import { EyeFilled, EyeInvisibleFilled, LeftOutlined } from "@ant-design/icons";
import logo from "../../assets/logo-dark.svg";
import { Button, Checkbox, Form, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import React from "react";
import { useDispatch } from "react-redux";
import ErrorAlert from "src/components/ErrorAlert/ErrorAlert";
import WelcomeSplash from "src/components/WelcomeSplash/WelcomeSplash";
import logoDark from "../../assets/logo192.png";
import { useLoginMutation } from "src/store/apis/Login";
import { login } from "src/store/slices/auth";
import { LoginModelReqTypes } from "src/models/LoginModel";
import { jwtDecode } from "jwt-decode";
import UserModel from "src/models/UserModel";
import CurrentProgramHeader from "src/components/CurrentProgramHeader";

interface LoginFormProps extends LoginModelReqTypes {
  rememberMe: boolean;
}

const currentYear = new Date().getFullYear();

export default function Login() {
  const [loginUser, { isError, isLoading, error }] = useLoginMutation();
  const [form] = Form.useForm<LoginFormProps>();

  const [submissionError, setSubmissionError] = React.useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onChange = () => {
    setSubmissionError(false);
  };

  const onFinish = async (values: LoginFormProps) => {
    try {
      const { result, errors } = await loginUser({
        userName: values.userName,
        password: values.password,
        programId: values.programId,
      }).unwrap();

      if (errors) {
        setSubmissionError(true);
        return;
      }

      const user: UserModel = jwtDecode(result.token);

      dispatch(
        login({
          user: {
            Client: user.Client,
            permission: user.permission,
          },
          accessToken: result.token,
          rememberMe: values.rememberMe,
        }),
      );

      navigate("/");
    } catch (error) {
      setSubmissionError(true);
      return;
    }
  };

  return (
    <div className="px-16 lg:px-32 flex flex-col justify-between h-screen">
      <div className="flex items-center pt-8 gap-4">
        <Link className="hover:text-primary-hover transition" to="/program">
          <LeftOutlined />
        </Link>
        <CurrentProgramHeader />
      </div>
      <div className="h-full lg:grid grid-cols-2 place-content-center w-full">
        <div>
          <WelcomeSplash />
        </div>
        <div className="flex gap-8 w-full flex-col lg:px-32">
          <Form<LoginFormProps>
            form={form}
            initialValues={{ rememberMe: false, programId: null }}
            onFinish={onFinish}
            onValuesChange={onChange}
          >
            <div className="space-y-6">
              <h1 className="text-2xl font-semibold text-dark">Login</h1>
              <Form.Item
                name="userName"
                rules={[
                  {
                    required: true,
                    message: (
                      <ErrorAlert message="Please enter your username" />
                    ),
                  },
                ]}
              >
                <Input
                  disabled={isLoading}
                  id="userName"
                  className="input-field-ant"
                  placeholder="Username"
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: (
                      <ErrorAlert message="Please enter your password" />
                    ),
                  },
                ]}
              >
                <Input.Password
                  disabled={isLoading}
                  type="password"
                  id="password"
                  className="input-field-ant"
                  placeholder="Password"
                  iconRender={(visible) =>
                    visible ? (
                      <EyeFilled style={{ fontSize: "1.5rem" }} />
                    ) : (
                      <EyeInvisibleFilled style={{ fontSize: "1.5rem" }} />
                    )
                  }
                  visibilityToggle
                />
              </Form.Item>
              {isError && submissionError && (
                <ErrorAlert
                  message={
                    (isError && (error as any).data?.message) ||
                    "Something went wrong"
                  }
                />
              )}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="button w-full bg-accent text-white disabled:bg-gray-400 bg-primary"
                  disabled={isLoading}
                  loading={isLoading}
                >
                  Login
                </Button>
              </Form.Item>
              <div className="mt-5 flex w-full flex-col items-center justify-between gap-4 xs:flex-row xs:gap-0">
                <Form.Item name="rememberMe" valuePropName="checked" noStyle>
                  <label className="text-center text-heading-1 font-medium text-primary">
                    <Checkbox id="remember-me" /> Remember me
                  </label>
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <img
          src={logo}
          alt="Dsquares"
          className="hidden lg:flex h-[2.3125rem] w-[12.25rem] flex-shrink-0 flex-col"
        />
        <img
          src={logoDark}
          alt="Dsquares"
          className="lg:hidden max-h-[2.5rem] max-w-[12.25rem] flex-shrink-0"
        />
        <p className="self-center py-8 text-16 font-medium text-[#230871]">
          © {currentYear} All Rights Reserved
        </p>
      </div>
    </div>
  );
}
